import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationBell.css';
import { useStore } from '../../store/useStore';
import { useSound } from '../../hooks/useSound';
import NotificationDropdown from './NotificationDropdown';
import io from 'socket.io-client';
import { useBroadcast } from '../../contexts/BroadcastContext';

// Иконка колокольчика (SVG inline)
const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  icon: string;
  data: any;
  read: boolean;
  created_at: string;
}

const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useStore();
  const playButtonClick = useSound('ui/ui_button_click.mp3');
  const playNotification = useSound('ui/notification.mp3'); // Добавим звук уведомления

  // Подключаем broadcast-уведомления (normal/low priority)
  const { notifications: broadcastNotifications, unreadCount: broadcastUnreadCount, markAsRead: markBroadcastAsRead } = useBroadcast();

  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [prevBroadcastCount, setPrevBroadcastCount] = useState(0);

  const bellRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<any>(null);

  // Загрузка количества непрочитанных (общие уведомления - ранги, купоны, и т.д.)
  const fetchUnreadCount = async () => {
    try {
      const response = await fetch('/api/notifications/unread', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newCount = data.unread_count || 0;

        // Если количество увеличилось - новое уведомление
        if (newCount > unreadCount) {
          setHasNewNotification(true);
          playNotification?.();

          // Убираем анимацию через 2 секунды
          setTimeout(() => setHasNewNotification(false), 2000);
        }

        setUnreadCount(newCount);
      }
    } catch (error) {
      console.error('Ошибка загрузки счетчика уведомлений:', error);
    }
  };

  // Загрузка списка уведомлений (общие уведомления - ранги, купоны и т.д.)
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/notifications?limit=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Формат уже соответствует нашему интерфейсу
        const formattedNotifications = (data.notifications || []).map((n: any) => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message,
          icon: n.icon || getNotificationIcon(n.type),
          data: n.data,
          read: n.read,
          created_at: n.created_at
        }));
        setNotifications(formattedNotifications);
      }
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  // Получить заголовок уведомления по типу
  const getNotificationTitle = (type: string): string => {
    const titles: Record<string, string> = {
      // Ранги и купоны
      'rank_up': `🎖️ ${t('notifications.rankUp')}`,
      'rank_upgrade': `🎖️ ${t('notifications.rankUp')}`,
      'coupon_received': `🎁 ${t('notifications.couponReceived')}`,
      'boost_activated': `⚡ ${t('notifications.boostActivated')}`,
      // Марафон
      'period_reminder': `🔔 ${t('notifications.periodReminder')}`,
      'payment_due': `⚠️ ${t('notifications.paymentDue')}`,
      'payment_success': `✅ ${t('notifications.paymentSuccess')}`,
      'payment_missed': `❌ ${t('notifications.paymentMissed')}`,
      'period_complete': `🎉 ${t('notifications.periodComplete')}`,
      'marathon_complete': `🏆 ${t('notifications.marathonComplete')}`,
      // Реферальная система
      'referral_bonus': `👥 ${t('notifications.referralBonus')}`,
      'referral_daily': `💰 ${t('notifications.referralDaily')}`,
      'referral_joined': `🤝 ${t('notifications.referralJoined')}`,
      // Стейкинг
      'staking_reward': `💎 ${t('notifications.stakingReward')}`,
      'claim_streak': `🔥 ${t('notifications.claimStreak')}`
    };
    return titles[type] || `🔔 ${t('notifications.default')}`;
  };

  // Получить иконку уведомления по типу
  const getNotificationIcon = (type: string): string => {
    const icons: Record<string, string> = {
      // Ранги и купоны
      'rank_up': '🎖️',
      'rank_upgrade': '🎖️',
      'coupon_received': '🎁',
      'boost_activated': '⚡',
      // Марафон
      'period_reminder': '🔔',
      'payment_due': '⚠️',
      'payment_success': '✅',
      'payment_missed': '❌',
      'period_complete': '🎉',
      'marathon_complete': '🏆',
      // Реферальная система
      'referral_bonus': '👥',
      'referral_daily': '💰',
      'referral_joined': '🤝',
      // Стейкинг
      'staking_reward': '💎',
      'claim_streak': '🔥'
    };
    // Handle referral_level_X_registered and referral_level_X_staking patterns
    if (type.startsWith('referral_level_') && type.endsWith('_registered')) return '🤝';
    if (type.startsWith('referral_level_') && type.endsWith('_staking')) return '💎';
    if (type.startsWith('referral_level_') && type.endsWith('_deposit')) return '💰';
    if (type === 'referral_missed_reward' || type === 'referral_missed_daily' || type === 'referral_missed_usdt_reward') return '⚠️';
    return icons[type] || '🔔';
  };

  // Отметить уведомление как прочитанное
  const markAsRead = async (notificationId: number) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);

        // Обновляем локальное состояние
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      }
    } catch (error) {
      console.error('Ошибка отметки уведомления:', error);
    }
  };

  // Отметить все как прочитанные
  const markAllAsRead = async () => {
    try {
      const response = await fetch('/api/notifications/read-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
    } catch (error) {
      console.error('Ошибка отметки всех уведомлений:', error);
    }
  };

  // Удалить уведомление
  const deleteNotification = async (notificationId: number) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        fetchUnreadCount(); // Обновляем счетчик
      }
    } catch (error) {
      console.error('Ошибка удаления уведомления:', error);
    }
  };

  // Отслеживаем новые broadcast-уведомления
  useEffect(() => {
    if (broadcastUnreadCount > prevBroadcastCount) {
      // Новое broadcast-уведомление
      setHasNewNotification(true);
      playNotification?.();
      setTimeout(() => setHasNewNotification(false), 2000);
      console.log('🔔 Новое broadcast-уведомление в колокольчике:', broadcastUnreadCount);
    }
    setPrevBroadcastCount(broadcastUnreadCount);
  }, [broadcastUnreadCount]);

  // Загрузка при монтировании и подписка на WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user || token) {
      fetchUnreadCount();

      // Подключаемся к WebSocket
      const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      socketRef.current = socket;

      // Подписываемся на уведомления
      socket.emit('notifications:subscribe');

      // Слушаем новые уведомления
      socket.on('notification:new', (notification: Notification) => {
        console.log('📬 Получено новое уведомление:', notification);

        // Добавляем в список
        setNotifications(prev => [notification, ...prev]);

        // Увеличиваем счетчик
        setUnreadCount(prev => prev + 1);

        // Анимация и звук
        setHasNewNotification(true);
        playNotification?.();
        setTimeout(() => setHasNewNotification(false), 2000);
      });

      // Слушаем обновления счетчика
      socket.on('notification:count', (data: { unread_count: number }) => {
        console.log('🔢 Обновлен счетчик уведомлений:', data.unread_count);
        setUnreadCount(data.unread_count);
      });

      // Обновляем счетчик каждые 30 секунд (fallback на случай проблем с WebSocket)
      const interval = setInterval(fetchUnreadCount, 30000);

      return () => {
        clearInterval(interval);
        socket.emit('notifications:unsubscribe');
        socket.disconnect();
      };
    }
  }, [user]);

  // Загружаем список при открытии
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (showDropdown && (user || token)) {
      fetchNotifications();
    }
  }, [showDropdown, user]);

  // Закрытие по клику вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        bellRef.current &&
        dropdownRef.current &&
        !bellRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleBellClick = () => {
    playButtonClick?.();
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="notification-bell-container" ref={bellRef}>
      <button
        className={`notification-bell-btn ${hasNewNotification ? 'shake' : ''}`}
        onClick={handleBellClick}
        aria-label={t('notifications.title')}
      >
        <BellIcon />
        {(unreadCount + broadcastUnreadCount) > 0 && (
          <span className="notification-badge">
            {(unreadCount + broadcastUnreadCount) > 99 ? '99+' : (unreadCount + broadcastUnreadCount)}
          </span>
        )}
      </button>

      {showDropdown && (
        <div ref={dropdownRef}>
          <NotificationDropdown
            notifications={[
              // Сначала broadcast-уведомления (normal/low priority)
              ...broadcastNotifications.map(b => ({
                id: b.id,
                type: 'broadcast',
                title: b.title || t('notifications.default'),
                message: b.message,
                icon: '📢',
                data: { buttonText: b.buttonText, buttonUrl: b.buttonUrl },
                read: false,
                created_at: new Date().toISOString()
              })),
              // Затем обычные уведомления
              ...notifications
            ]}
            loading={loading}
            unreadCount={unreadCount + broadcastUnreadCount}
            onMarkAsRead={(id) => {
              // Проверяем, это broadcast или обычное уведомление
              const isBroadcast = broadcastNotifications.some(b => b.id === id);
              if (isBroadcast) {
                markBroadcastAsRead(id);
              } else {
                markAsRead(id);
              }
            }}
            onMarkAllAsRead={() => {
              markAllAsRead();
              // Также помечаем все broadcasts как прочитанные
              broadcastNotifications.forEach(b => markBroadcastAsRead(b.id));
            }}
            onDelete={deleteNotification}
            onClose={() => setShowDropdown(false)}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
